import type { SessionParent, WorktreeBaseBranches } from '@buildautomaton/types';
import type { LogFn } from '../../log.js';
import type { WorktreeLayoutFile } from '../worktree-layout-file.js';
import type { SessionWorktreeBinding } from '../session-worktree-binding.js';
import { parseSessionParent } from './parse-session-parent.js';
import { prepareAndRememberSessionWorktrees } from './prepare-and-remember-session-worktrees.js';
import { resolveExistingSessionParentPath } from './resolve-existing-session-parent-path.js';
import { resolveExplicitSessionParentPath } from './resolve-explicit-session-parent-path.js';
import type { SessionWorktreeCache } from './session-worktree-cache.js';

export async function resolveSessionParentPathForPrompt(params: {
  sessionId: string | undefined;
  cache: SessionWorktreeCache;
  worktreesRootPath: string;
  layout: WorktreeLayoutFile;
  log: LogFn;
  discover: (sessionId: string) => Promise<SessionWorktreeBinding | null>;
  opts: {
    isNewSession: boolean;
    sessionParent: SessionParent | null | undefined;
    sessionParentPath: string | null | undefined;
    worktreeBaseBranches?: WorktreeBaseBranches;
  };
}): Promise<string | undefined> {
  const { sessionId, cache, worktreesRootPath, layout, log, discover, opts } = params;
  if (!sessionId) return undefined;
  const sid = sessionId.trim();

  const parentPathRaw = opts.sessionParentPath?.trim();
  if (parentPathRaw) {
    return resolveExplicitSessionParentPath({
      sessionId: sid,
      sessionParent: opts.sessionParent,
      parentPathRaw,
      cache,
      discover: () => discover(sid),
    });
  }

  const parentKind = parseSessionParent(opts.sessionParent);

  if (parentKind === 'bridge_root') {
    return undefined;
  }

  if (parentKind === 'worktrees_root') {
    if (!opts.isNewSession) {
      return resolveExistingSessionParentPath(sid, cache, () => discover(sid));
    }
    return prepareAndRememberSessionWorktrees({
      cache,
      sessionId: sid,
      worktreesRootPath,
      layout,
      log,
      ...(opts.worktreeBaseBranches ? { worktreeBaseBranches: opts.worktreeBaseBranches } : {}),
    });
  }

  if (!opts.isNewSession) {
    return resolveExistingSessionParentPath(sid, cache, () => discover(sid));
  }

  return prepareAndRememberSessionWorktrees({
    cache,
    sessionId: sid,
    worktreesRootPath,
    layout,
    log,
  });
}
